import React, { useEffect, useMemo, useState } from "react";
import { ProgressBar } from "@shared/ui.components";

import { DeprecatedOverlayDrawer } from "packages/ui-shared/components/OverlayDrawer";
import { Header } from "./components/Header";
import {
  contentContainer,
  ctaWrapper,
  overlayWrapper,
  wrapper
} from "./DiscoveryJourneyModal.css";
import { useDiscoveryJourney } from "./useDiscoveryJourney";
// eslint-disable-next-line import/no-unresolved -- clash with /slides directory
import { slides } from "./components/Slides";
import { useUser } from "lib/user";
import { useUserContext } from "@shared/ui.hooks";
import { activeMemberships } from "lib/apollo/constants";

export const MEMBERSHIP_SKUS = {
  membershipsWithPerks: [
    "BP_PLUS_UK",
    "BP_PLUS_US",
    activeMemberships["en-GB"].plusMembershipId,
    activeMemberships["en-US"].plusMembershipId
  ],
  freeTrial: [
    "BP_TRIAL_60D_UK",
    "BP_TRIAL_60D_US",
    activeMemberships["en-GB"].sixtyDayFreeTrialRenewsToAnnualMembershipId,
    activeMemberships["en-US"].sixtyDayFreeTrialRenewsToAnnualMembershipId
  ]
};

type Props = {
  isVisible: boolean;
  setNextVisibility: (visible: boolean, callback?: () => void) => void;
};

export const DiscoveryJourneyModal: React.FC<Props> = ({
  isVisible,
  setNextVisibility
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const { slides: journeySlides } = useDiscoveryJourney();
  const [slideId, setSlideId] = useState(journeySlides[0]?.id);
  const { isLoggedIn, membership } = useUser();
  const { type: memberType } = useUserContext();

  const hasMembershipWithPerks = useMemo(
    () =>
      !!membership?.id &&
      MEMBERSHIP_SKUS.membershipsWithPerks.includes(membership.id),
    [membership?.id]
  );
  const hasFreeTrialMembership = useMemo(
    () => !!membership?.id && MEMBERSHIP_SKUS.freeTrial.includes(membership.id),
    [membership?.id]
  );
  const isInGracePeriod = useMemo(() => {
    if (typeof window === "undefined") return false;
    return !!window?.sessionStorage?.getItem("failed-renewal-modal");
  }, []);

  const isValidUserContext = useMemo(() => {
    return (
      isLoggedIn &&
      !isInGracePeriod &&
      (hasFreeTrialMembership ||
        hasMembershipWithPerks ||
        memberType === "lapsedmember")
    );
  }, [
    isLoggedIn,
    isInGracePeriod,
    hasFreeTrialMembership,
    hasMembershipWithPerks,
    memberType
  ]);

  const backToPreviousSlide = () => setCurrentSlideIndex(currentSlideIndex - 1);
  const goToNextSlide = () => setCurrentSlideIndex(currentSlideIndex + 1);

  useEffect(() => {
    setSlideId(journeySlides[currentSlideIndex]?.id);
  }, [currentSlideIndex, journeySlides]);

  const content = slides({
    slides: journeySlides,
    currentSlideIndex,
    backToPreviousSlide,
    goToNextSlide,
    setNextVisibility
  });

  if (
    isValidUserContext &&
    Array.isArray(journeySlides) &&
    journeySlides.length > 0
  ) {
    return (
      <DeprecatedOverlayDrawer
        isOpen={isVisible}
        className={overlayWrapper}
        data-testid="discovery-modal"
      >
        <div className={wrapper}>
          <Header
            handleCloseModal={() => setNextVisibility(false)}
            slideId={slideId}
          />
          <ProgressBar
            currentStep={currentSlideIndex + 1}
            totalSteps={journeySlides.length}
          />
          <div className={contentContainer}>{content.component}</div>
          <div>
            <div className={ctaWrapper}>{content.cta}</div>
          </div>
        </div>
      </DeprecatedOverlayDrawer>
    );
  }

  return null;
};
